import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { endOfMonth } from 'date-fns'
import {
  SalaryAdvanceRequestFragment,
  SalaryAdvanceRequestsCollectionDocument,
  SalaryAdvanceRequestsCollectionQuery,
  SalaryAdvanceValueObjectsRequestStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { getStatusLabel, getStatusVariant } from 'modules/salary_advance/lib/requestHelpers'

const DEFAULT_PER_PAGE = 25
export const RequestsTable = () => {
  const { access } = useCurrentSession()
  const locale = access.locale || 'en'

  const emptyState = {
    'no-data': {
      title: i18n.t('salary_advance.request.table.empty_state.title'),
      description: i18n.t('salary_advance.request.table.empty_state.description'),
    },
  }

  const requestStatusOptions: Array<{
    label: string
    value: SalaryAdvanceValueObjectsRequestStatusEnum
  }> = [
    {
      label: getStatusLabel('requested'),
      value: 'requested',
    },
    {
      label: getStatusLabel('processing'),
      value: 'processing',
    },
    {
      label: getStatusLabel('completed'),
      value: 'completed',
    },
  ]

  const source = useGraphqlDataCollectionSource({
    document: SalaryAdvanceRequestsCollectionDocument,
    fetchPolicy: 'cache-and-network' as const,
    perPage: DEFAULT_PER_PAGE,
    mapVariables: ({ filters, navigationFilters, search, sortings }) => {
      return {
        dateFrom: navigationFilters?.date?.value?.from,
        dateTo: navigationFilters?.date?.value?.to,
        sortOrder: sortings?.[0],
        requestStatuses: filters?.status,
        search,
      }
    },
    search: {
      enabled: true,
    },
    totalItemSummary: (totalItems) => {
      if (totalItems === 0) {
        return i18n.t('salary_advance.request.table.zero_items')
      }
      return i18n.tp('salary_advance.request.table.total_items', {
        count: totalItems,
      })
    },
    navigationFilters: {
      date: {
        type: 'date-navigator',
        defaultValue: new Date(),
        hideGoToCurrent: true,
        defaultGranularity: 'month',
        granularity: ['week', 'month', 'day'],
        max: endOfMonth(new Date()),
      },
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    sortings: {
      createdAt: {
        label: i18n.t('common.date.date'),
      },
    },
    presets: [
      {
        label: i18n.t('common.all'),
        filter: {},
      },
      {
        label: i18n.t('salary_advance.request.status.requested'),
        filter: { status: ['requested'] },
      },
      {
        label: i18n.t('salary_advance.request.status.processing'),
        filter: { status: ['processing'] },
      },
      {
        label: i18n.t('salary_advance.request.status.completed'),
        filter: { status: ['completed'] },
      },
    ],
    filters: {
      status: {
        type: 'in',
        label: i18n.t('common.status'),
        options: {
          options: requestStatusOptions,
        },
      },
    },
    mapData: (data: SalaryAdvanceRequestsCollectionQuery) => {
      return data.salaryAdvance.requestsConnection.edges.map((edge) => edge.node)
    },
    mapPagination: (data: SalaryAdvanceRequestsCollectionQuery) => {
      const count = data.salaryAdvance.requestsConnection.totalCount ?? 0
      return { total: count }
    },
  })

  return (
    <OneDataCollection
      source={source}
      emptyStates={emptyState}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('common.employee'),
                render: (item: SalaryAdvanceRequestFragment) => {
                  if (!item.employeeName) {
                    return { type: 'text', value: '-' }
                  }

                  return {
                    type: 'person',
                    value: {
                      firstName: item.employeeName.firstName,
                      lastName: item.employeeName.lastName,
                      src: buildUrl(item.employeeName.avatar?.url ?? ''),
                    },
                  }
                },
              },
              {
                label: i18n.t('common.date.date'),
                sorting: 'createdAt',
                render: (item: SalaryAdvanceRequestFragment) => {
                  return {
                    type: 'date',
                    value: convertDate(item.createdAt, 'backend_datetime', 'ui'),
                  }
                },
              },
              {
                label: i18n.t('salary_advance.request.table.columns.requested_amount'),
                render: (item: SalaryAdvanceRequestFragment) => {
                  return {
                    type: 'text',
                    value: MoneyWithCurrency({
                      amount: item.amountCents,
                      currency: item.currency,
                      locale,
                    }),
                  }
                },
              },
              {
                label: i18n.t('common.status'),
                render: (item: SalaryAdvanceRequestFragment) => {
                  return {
                    type: 'status',
                    value: {
                      label: getStatusLabel(item.status),
                      status: getStatusVariant(item.status),
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
